export const ArrayUtils = {
  sortNumberArray(array: number[]): number[] {
    return [...array].sort((a, b) => a - b);
  },
  sortNumberArrayDescending(array: number[]): number[] {
    return [...array].sort((a, b) => b - a);
  },
  isContiguous(array: number[]): boolean {
    const firstNumber = array[0];
    const lastNumber = array[array.length - 1];

    /* In a contiguous array, subtracting the first number from 
      the last should match the array length minus 1 */
    if (Math.abs(lastNumber - firstNumber) !== array.length - 1) {
      return false;
    }

    // A contiguous array should be the same order when sorted
    const sorted = this.sortNumberArray(array);
    if (sorted.every((val, index) => array[index] === val)) {
      return true;
    }

    // It doesn't match ascending, finally check if it matches in descending order
    const sortedDesc = this.sortNumberArrayDescending(array);
    return sortedDesc.every((val, index) => array[index] === val);
  },
  extractContiguousBlock(array: number[]) {
    const block = [];

    for (let index = 0; index < array.length; index++) {
      const currentValue = array[index];
      block.push(currentValue);

      // If the next value is not contiguous then we return what we've got so far
      const nextIndex = index + 1;
      if (currentValue + 1 !== array[nextIndex] && currentValue - 1 !== array[nextIndex]) {
        return {
          block,
          nextIndex
        };
      }
    }
  },
  getContiguousText(sourceArray: number[], separator: string = ', '): string {
    if (!sourceArray.length) {
      return '';
    }

    const values = [];
    let index = 0;

    while (index < sourceArray.length) {
      const slice = [...sourceArray].slice(index);

      const extracted = this.extractContiguousBlock(slice);
      if (!extracted || !extracted.block.length) {
        break;
      }

      if (extracted.block.length === 1) {
        values.push(extracted.block[0]);
      } else {
        // Page ranges should be hyphenated
        values.push(`${extracted.block[0]}-${extracted.block[extracted.block.length - 1]}`);
      }

      index += extracted.nextIndex;
    }

    return values.join(separator);
  },
  getRangeArray(rangeStart: number, rangeEnd: number): number[] {
    if (rangeStart > rangeEnd) {
      return [];
    }
    return [...Array(rangeEnd - rangeStart + 1)].map((_, i) => rangeStart + i);
  },
  getPageNumbersArray(pages: string): number[] {
    const pageNumbers = pages?.split(',').reduce((list: number[], page: string) => {
      const pageNumber = Number(page);
      if (pageNumber === 0) {
        return list;
      }
      if (!page?.includes('-')) {
        list.push(pageNumber);
      } else {
        const rangeString = page?.split('-');
        list = [...list, ...this.getRangeArray(Number(rangeString[0]), Number(rangeString[1]))];
      }
      return list;
    }, []);
    return pageNumbers;
  },
  removePageFromSelection(pageSelection: string, pageToRemove: number): string {
    let pagesArray = ArrayUtils.getPageNumbersArray(pageSelection);

    if (pagesArray.some((x) => x >= pageToRemove)) {
      const deletedPageIndex = pagesArray.findIndex((x) => x === pageToRemove);
      if (deletedPageIndex >= 0) {
        pagesArray.splice(deletedPageIndex, 1);
      }

      pagesArray = pagesArray.map((pageNumber) => {
        return pageNumber < pageToRemove ? pageNumber : pageNumber - 1;
      });

      return pagesArray.length ? ArrayUtils.getContiguousText(pagesArray, ',') : '1';
    }

    return pageSelection;
  },
  immutableSplice(array: any[], start: number, deleteCount: number) {
    const newArray = [...array];
    newArray.splice(start, deleteCount);
    return newArray;
  },
  alphabeticalSort<Type extends string | object>(list: Type[], getter: (type: Type) => string = (type) => type as string) {
    return [...list].sort((a, b) => (getter(a) > getter(b) ? 1 : -1));
  }
};
